// frontend/src/pages/TimeTracking/ReportGeneratorMonthly.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Form, Select, Button, Typography, Row, Col, Space,
    message, Spin, Divider, Table, Tag, Descriptions, Empty,
    Alert, Collapse, Badge, DatePicker
} from 'antd';
import {
    FileTextOutlined,
    DownloadOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    EyeOutlined,
    RollbackOutlined,
    ClockCircleOutlined,
    UserOutlined,
    CalendarOutlined,
    HistoryOutlined,
    ReloadOutlined,
    PieChartOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import './TimeTrackingForm.css';

const { Title, Text } = Typography;
const { Option } = Select;

const ReportGeneratorMonthly = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const notificationApi = useNotificationApi();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);
    
    // FIX: Set initial month to null (blank) to prevent jumping
    const [selectedMonth, setSelectedMonth] = useState(null);

    // Notification helpers
    const showError = useCallback((title, content, key = 'reportError') => {
        notificationApi.destroy(key);
        notificationApi.error({
            key,
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, description, key = 'reportSuccess') => {
        notificationApi.destroy(key);
        notificationApi.success({
            key,
            message: title,
            description,
            duration: 3,
            placement: 'topRight',
        });
    }, [notificationApi]);

    // Fetch contracts
    useEffect(() => {
        const fetchContracts = async () => {
            try {
                const response = await api.get('/api/contracts');
                if (response.data.success) {
                    setContracts(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching contracts:', error);
                showError('Failed to Load', 'Could not load contracts.');
            }
        };
        fetchContracts();
    }, [showError]);

    // Handle month change
    const handleMonthChange = (date) => {
        setSelectedMonth(date);
        form.setFieldsValue({ report_month: date });
    };

    // Generate monthly report
    const handleGenerateReport = async (values) => {
        // If no month selected, use current month
        const reportMonth = values.report_month || moment();
        const year = reportMonth.year();
        const month = reportMonth.month() + 1;
        const contractId = values.contract_id;

        if (!contractId) {
            message.warning('Please select a contract');
            return;
        }

        setGenerating(true);
        try {
            const response = await api.get('/api/time-tracking/report/monthly', {
                params: { 
                    contractId: contractId,
                    year, 
                    month 
                }
            });
            
            if (response.data.success) {
                const data = response.data.data;
                
                const formattedData = {
                    ...data,
                    grand_total: parseFloat(data.grand_total || 0),
                    grand_total_display: data.grand_total_display || '0 hrs',
                    grand_man_days: data.grand_man_days || 0,
                    grand_man_days_display: data.grand_man_days_display || '0 days',
                    cr_total_hours: parseFloat(data.cr_total_hours || 0),
                    cm_total_hours: parseFloat(data.cm_total_hours || 0),
                    pm_total_hours: parseFloat(data.pm_total_hours || 0),
                    oth_total_hours: parseFloat(data.oth_total_hours || 0),
                    cr_man_days: data.cr_man_days || 0,
                    cm_man_days: data.cm_man_days || 0,
                    pm_man_days: data.pm_man_days || 0,
                    oth_man_days: data.oth_man_days || 0,
                    // Summary lists from backend
                    cr_summary: data.cr_summary || [],
                    cm_summary: data.cm_summary || [],
                    pm_summary: data.pm_summary || [],
                    oth_summary: data.oth_summary || [],
                    generated_by: data.generated_by || (user?.firstName && user?.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : 'System'),
                    generated_date: data.generated_date || moment().tz('Asia/Kuala_Lumpur').format('DD/MM/YYYY HH:mm')
                };
                
                setReportData(formattedData);
                showSuccess('Monthly Report Generated', 'Monthly consolidated report generated successfully.');
            } else {
                showError('Generation Failed', response.data.message || 'Failed to generate report.');
            }
        } catch (error) {
            console.error('Error generating monthly report:', error);
            showError('Generation Failed', error.response?.data?.message || 'An error occurred while generating the report.');
        } finally {
            setGenerating(false);
        }
    };

    // Export to Word
    const exportToWord = async () => {
        if (!reportData) return;
        
        const contractId = form.getFieldValue('contract_id');
        const reportMonth = selectedMonth || moment();
        
        setExporting(true);
        try {
            const response = await api.post('/api/reports/time-tracking/cr/monthly', {
                contractId: contractId,
                year: reportMonth.year(),
                month: reportMonth.month() + 1,
                format: 'docx'
            }, {
                responseType: 'blob'
            });

            if (response.data instanceof Blob) {
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                
                const lastDayOfMonth = reportMonth.clone().endOf('month').format('DDMMYYYY');
                const monthName = reportMonth.format('MMM');
                const yearSuffix = reportMonth.format('YY');
                const contractName = reportData.contract_name || 'Contract';
                const cleanContractName = contractName.replace(/[^a-zA-Z0-9]/g, '_');
                
                const filename = `Monthly_${monthName}${yearSuffix}_${cleanContractName}_AsAt_${lastDayOfMonth}.docx`;
                
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                showSuccess('Export Successful', 'Word document downloaded successfully.');
            }
        } catch (error) {
            console.error('Word export error:', error);
            showError('Export Failed', 'Failed to export Word document.');
        } finally {
            setExporting(false);
        }
    };

    // Export to PDF
    const exportToPDF = async () => {
        if (!reportData) return;
        
        const contractId = form.getFieldValue('contract_id');
        const reportMonth = selectedMonth || moment();
        
        setExporting(true);
        try {
            const response = await api.post('/api/reports/time-tracking/cr/monthly', {
                contractId: contractId,
                year: reportMonth.year(),
                month: reportMonth.month() + 1,
                format: 'pdf'
            }, {
                responseType: 'blob'
            });

            if (response.data instanceof Blob) {
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                
                const lastDayOfMonth = reportMonth.clone().endOf('month').format('DDMMYYYY');
                const monthName = reportMonth.format('MMM');
                const yearSuffix = reportMonth.format('YY');
                const contractName = reportData.contract_name || 'Contract';
                const cleanContractName = contractName.replace(/[^a-zA-Z0-9]/g, '_');
                
                const filename = `Monthly_${monthName}${yearSuffix}_${cleanContractName}_AsAt_${lastDayOfMonth}.pdf`;
                
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                showSuccess('Export Successful', 'PDF downloaded successfully.');
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showError('Export Failed', 'Failed to export PDF.');
        } finally {
            setExporting(false);
        }
    };

    // Export to Excel (CSV)
    const exportToExcel = () => {
        if (!reportData) return;
        setExporting(true);
        try {
            const excelData = [];
            
            // Header
            excelData.push(['MONTHLY TIME TRACKING REPORT']);
            excelData.push([`${reportData.month_name} ${reportData.year}`]);
            excelData.push([`Contract: ${reportData.contract_name || 'Contract'}`]);
            excelData.push([]);
            
            // Grand Total
            excelData.push(['GRAND TOTAL', reportData.grand_total_display || '0 hrs']);
            excelData.push(['Man Days', reportData.grand_man_days_display || '0 days']);
            excelData.push([]);
            
            // Summary by Type
            excelData.push(['SUMMARY BY TYPE']);
            excelData.push(['CR', `${reportData.cr_total_hours || 0} hrs (${reportData.cr_man_days || 0} days)`]);
            excelData.push(['CM', `${reportData.cm_total_hours || 0} hrs (${reportData.cm_man_days || 0} days)`]);
            excelData.push(['PM', `${reportData.pm_total_hours || 0} hrs (${reportData.pm_man_days || 0} days)`]);
                        excelData.push(['NCM', `${reportData.oth_total_hours || 0} hrs (${reportData.oth_man_days || 0} days)`]);
            excelData.push([]);
            
            // CR Summary
            if (reportData.cr_summary && reportData.cr_summary.length > 0) {
                excelData.push(['ACTIVITY CR']);
                excelData.push(['Ticket UUID', 'Ticket Title', 'CR/CM #', 'Description', 'Request Date', 'Entries', 'Total Hours']);
                reportData.cr_summary.forEach(item => {
                    excelData.push([
                        item.cr_ticket_uuid || '-',
                        item.cr_ticket_title || '-',
                        item.cr_number || '-',
                        item.cr_description || '-',
                        item.cr_request_date || '-',
                        item.cr_entry_count || 0,
                        item.cr_total_hours || 0
                    ]);
                });
                excelData.push(['TOTAL:', '', '', '', '', '', `${reportData.cr_total_hours || 0} hrs`]);
                excelData.push(['Man Days:', '', '', '', '', '', `${reportData.cr_man_days || 0} days`]);
                excelData.push([]);
            }
            
            // CM Summary
            if (reportData.cm_summary && reportData.cm_summary.length > 0) {
                excelData.push(['ACTIVITY CM']);
                excelData.push(['Ticket UUID', 'Ticket Title', 'CR/CM #', 'Description', 'Request Date', 'Entries', 'Total Hours']);
                reportData.cm_summary.forEach(item => {
                    excelData.push([
                        item.cm_ticket_uuid || '-',
                        item.cm_ticket_title || '-',
                        item.cm_number || '-',
                        item.cm_description || '-',
                        item.cm_request_date || '-',
                        item.cm_entry_count || 0,
                        item.cm_total_hours || 0
                    ]);
                });
                excelData.push(['TOTAL:', '', '', '', '', '', `${reportData.cm_total_hours || 0} hrs`]);
                excelData.push(['Man Days:', '', '', '', '', '', `${reportData.cm_man_days || 0} days`]);
                excelData.push([]);
            }
            
            // PM Summary
            if (reportData.pm_summary && reportData.pm_summary.length > 0) {
                excelData.push(['ACTIVITY PM']);
                excelData.push(['Ticket UUID', 'Ticket Title', 'CR/CM #', 'Description', 'Request Date', 'Entries', 'Total Hours']);
                reportData.pm_summary.forEach(item => {
                    excelData.push([
                        item.pm_ticket_uuid || '-',
                        item.pm_ticket_title || '-',
                        item.pm_number || '-',
                        item.pm_description || '-',
                        item.pm_request_date || '-',
                        item.pm_entry_count || 0,
                        item.pm_total_hours || 0
                    ]);
                });
                excelData.push(['TOTAL:', '', '', '', '', '', `${reportData.pm_total_hours || 0} hrs`]);
                excelData.push(['Man Days:', '', '', '', '', '', `${reportData.pm_man_days || 0} days`]);
                excelData.push([]);
            }
            
            // NCM Summary
            if (reportData.oth_summary && reportData.oth_summary.length > 0) {
                excelData.push(['ACTIVITY NCM']);
                excelData.push(['Ticket UUID', 'Ticket Title', 'CR/CM #', 'Description', 'Request Date', 'Entries', 'Total Hours']);
                reportData.oth_summary.forEach(item => {
                    excelData.push([
                        item.oth_ticket_uuid || '-',
                        item.oth_ticket_title || '-',
                        item.oth_number || '-',
                        item.oth_description || '-',
                        item.oth_request_date || '-',
                        item.oth_entry_count || 0,
                        item.oth_total_hours || 0
                    ]);
                });
                excelData.push(['TOTAL:', '', '', '', '', '', `${reportData.oth_total_hours || 0} hrs`]);
                excelData.push(['Man Days:', '', '', '', '', '', `${reportData.oth_man_days || 0} days`]);
                excelData.push([]);
            }
            
            excelData.push(['Generated by:', reportData.generated_by || 'System']);
            excelData.push(['Date:', reportData.generated_date || moment().tz('Asia/Kuala_Lumpur').format('DD/MM/YYYY HH:mm')]);
            
            const csvContent = excelData.map(row => 
                row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
            ).join('\n');
            
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            const reportMonth = selectedMonth || moment();
            const lastDayOfMonth = reportMonth.clone().endOf('month').format('DDMMYYYY');
            const monthName = reportMonth.format('MMM');
            const yearSuffix = reportMonth.format('YY');
            const contractName = reportData.contract_name || 'Contract';
            const cleanContractName = contractName.replace(/[^a-zA-Z0-9]/g, '_');
            
            link.download = `Monthly_${monthName}${yearSuffix}_${cleanContractName}_AsAt_${lastDayOfMonth}.csv`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            showSuccess('Export Successful', 'Excel file downloaded successfully.');
        } catch (error) {
            console.error('Excel export error:', error);
            showError('Export Failed', 'Failed to export Excel file.');
        } finally {
            setExporting(false);
        }
    };

    // FIX: Simple display - the backend already formats dates as DD/MM/YYYY
    const displayDate = (dateStr) => {
        if (!dateStr || dateStr === 'N/A' || dateStr === '-') return '-';
        return dateStr; // Backend already formats as DD/MM/YYYY
    };

    // FIX: CR Summary columns (Request Date already formatted by backend)
    const crSummaryColumns = [
        {
            title: 'Ticket UUID',
            dataIndex: 'cr_ticket_uuid',
            key: 'cr_ticket_uuid',
            width: 200,
            render: (text) => <Text code>{text || '-'}</Text>
        },
        {
            title: 'Ticket Title',
            dataIndex: 'cr_ticket_title',
            key: 'cr_ticket_title',
            ellipsis: true,
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'CR/CM #',
            dataIndex: 'cr_number',
            key: 'cr_number',
            width: 100,
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'cr_description',
            key: 'cr_description',
            ellipsis: true,
            width: 250,
            render: (text) => text || '-'
        },
        {
            title: 'Request Date',
            dataIndex: 'cr_request_date',
            key: 'cr_request_date',
            width: 120,
            render: (text) => displayDate(text)
        },
        {
            title: 'Entries',
            dataIndex: 'cr_entry_count',
            key: 'cr_entry_count',
            width: 80,
            align: 'center',
            render: (text) => <Tag color="blue">{text || 0}</Tag>
        },
        {
            title: 'Total Hours',
            dataIndex: 'cr_total_hours',
            key: 'cr_total_hours',
            width: 100,
            align: 'center',
            render: (text) => <Text strong>{text || 0} hrs</Text>
        }
    ];

    // CM Summary columns
    const cmSummaryColumns = [
        {
            title: 'Ticket UUID',
            dataIndex: 'cm_ticket_uuid',
            key: 'cm_ticket_uuid',
            width: 200,
            render: (text) => <Text code>{text || '-'}</Text>
        },
        {
            title: 'Ticket Title',
            dataIndex: 'cm_ticket_title',
            key: 'cm_ticket_title',
            ellipsis: true,
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'CR/CM #',
            dataIndex: 'cm_number',
            key: 'cm_number',
            width: 100,
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'cm_description',
            key: 'cm_description',
            ellipsis: true,
            width: 250,
            render: (text) => text || '-'
        },
        {
            title: 'Request Date',
            dataIndex: 'cm_request_date',
            key: 'cm_request_date',
            width: 120,
            render: (text) => displayDate(text)
        },
        {
            title: 'Entries',
            dataIndex: 'cm_entry_count',
            key: 'cm_entry_count',
            width: 80,
            align: 'center',
            render: (text) => <Tag color="blue">{text || 0}</Tag>
        },
        {
            title: 'Total Hours',
            dataIndex: 'cm_total_hours',
            key: 'cm_total_hours',
            width: 100,
            align: 'center',
            render: (text) => <Text strong>{text || 0} hrs</Text>
        }
    ];

    // PM Summary columns
    const pmSummaryColumns = [
        {
            title: 'Ticket UUID',
            dataIndex: 'pm_ticket_uuid',
            key: 'pm_ticket_uuid',
            width: 200,
            render: (text) => <Text code>{text || '-'}</Text>
        },
        {
            title: 'Ticket Title',
            dataIndex: 'pm_ticket_title',
            key: 'pm_ticket_title',
            ellipsis: true,
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'CR/CM #',
            dataIndex: 'pm_number',
            key: 'pm_number',
            width: 100,
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'pm_description',
            key: 'pm_description',
            ellipsis: true,
            width: 250,
            render: (text) => text || '-'
        },
        {
            title: 'Request Date',
            dataIndex: 'pm_request_date',
            key: 'pm_request_date',
            width: 120,
            render: (text) => displayDate(text)
        },
        {
            title: 'Entries',
            dataIndex: 'pm_entry_count',
            key: 'pm_entry_count',
            width: 80,
            align: 'center',
            render: (text) => <Tag color="blue">{text || 0}</Tag>
        },
        {
            title: 'Total Hours',
            dataIndex: 'pm_total_hours',
            key: 'pm_total_hours',
            width: 100,
            align: 'center',
            render: (text) => <Text strong>{text || 0} hrs</Text>
        }
    ];

    // OTHERS Summary columns
    const othSummaryColumns = [
        {
            title: 'Ticket UUID',
            dataIndex: 'oth_ticket_uuid',
            key: 'oth_ticket_uuid',
            width: 200,
            render: (text) => <Text code>{text || '-'}</Text>
        },
        {
            title: 'Ticket Title',
            dataIndex: 'oth_ticket_title',
            key: 'oth_ticket_title',
            ellipsis: true,
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'CR/CM #',
            dataIndex: 'oth_number',
            key: 'oth_number',
            width: 100,
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'oth_description',
            key: 'oth_description',
            ellipsis: true,
            width: 250,
            render: (text) => text || '-'
        },
        {
            title: 'Request Date',
            dataIndex: 'oth_request_date',
            key: 'oth_request_date',
            width: 120,
            render: (text) => displayDate(text)
        },
        {
            title: 'Entries',
            dataIndex: 'oth_entry_count',
            key: 'oth_entry_count',
            width: 80,
            align: 'center',
            render: (text) => <Tag color="blue">{text || 0}</Tag>
        },
        {
            title: 'Total Hours',
            dataIndex: 'oth_total_hours',
            key: 'oth_total_hours',
            width: 100,
            align: 'center',
            render: (text) => <Text strong>{text || 0} hrs</Text>
        }
    ];

    // Render a table section with optional hiding
    const renderTableSection = (title, icon, color, data, columns, totalHours, manDays, emptyMessage) => {
        const hasData = data && data.length > 0;
        
        return (
            <div style={{ marginBottom: 24 }}>
                <Divider orientation="left">
                    <Tag color={color} style={{ fontSize: '16px', padding: '4px 12px' }}>
                        {icon} {title}
                    </Tag>
                </Divider>
                {hasData ? (
                    <>
                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey={(record, index) => `${title}_${index}`}
                            pagination={false}
                            size="small"
                            bordered
                            style={{ marginBottom: 16 }}
                        />
                        <div style={{
                            background: color === 'blue' ? '#e6f7ff' : 
                                      color === 'green' ? '#f6ffed' : 
                                      color === 'gold' ? '#fffbe6' : '#fafafa',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 12
                        }}>
                            <div>
                                <Text strong>TOTAL:</Text>
                                <Text strong style={{ color: color === 'blue' ? '#1890ff' : 
                                                       color === 'green' ? '#52c41a' : 
                                                       color === 'gold' ? '#faad14' : '#8c8c8c', marginLeft: 8 }}>
                                    {totalHours || 0} hours
                                </Text>
                            </div>
                            <div>
                                <Text strong>Man Days:</Text>
                                <Text strong style={{ color: '#fa8c16', marginLeft: 8 }}>
                                    {manDays || 0} days
                                </Text>
                            </div>
                        </div>
                    </>
                ) : (
                    // FIX: Hide empty tables - show nothing instead of Empty component
                    null
                )}
            </div>
        );
    };

    return (
        <div className="time-tracking-page">
            <div className="time-tracking-header">
                <Title level={2} className="time-tracking-title">
                    <HistoryOutlined /> Monthly CR/CM Consolidated Report
                </Title>
                <Text className="time-tracking-description">
                    Generate a monthly consolidated report for all CR/CM activities by contract.
                    Shows total hours, man days, and breakdown per activity type.
                </Text>
            </div>

            <Card className="time-tracking-card">
                <div className="time-tracking-content">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleGenerateReport}
                        initialValues={{ report_month: null }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="contract_id"
                                    label="Contract"
                                    rules={[{ required: true, message: 'Please select a contract' }]}
                                >
                                    <Select
                                        placeholder="Select contract"
                                        showSearch
                                        optionFilterProp="children"
                                        size="large"
                                    >
                                        {contracts.map(contract => (
                                            <Option key={contract.id} value={contract.id}>
                                                {contract.contractName || `Contract #${contract.id}`}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    name="report_month"
                                    label="Month"
                                >
                                    <DatePicker
                                        picker="month"
                                        format="MMMM YYYY"
                                        style={{ width: '100%' }}
                                        size="large"
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                        placeholder="Select month"
                                        // FIX: Allow null value to prevent jumping
                                        allowClear={true}
                                        onPanelChange={(value) => {
                                            if (value && value.isValid()) {
                                                setSelectedMonth(value);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<EyeOutlined />}
                                    loading={generating}
                                    size="large"
                                >
                                    Generate Monthly Report
                                </Button>
                                <Button
                                    icon={<RollbackOutlined />}
                                    onClick={() => navigate(-1)}
                                    size="large"
                                >
                                    Back
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>

                    {reportData && (
                        <>
                            <Divider />

                            <div className="report-preview">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <Title level={4} style={{ margin: 0 }}>
                                        <HistoryOutlined /> Monthly Report Preview
                                    </Title>
                                    <Space>
                                        <Button
                                            icon={<FileWordOutlined />}
                                            onClick={exportToWord}
                                            loading={exporting}
                                        >
                                            Export Word
                                        </Button>
                                        <Button
                                            icon={<FilePdfOutlined />}
                                            onClick={exportToPDF}
                                            loading={exporting}
                                        >
                                            Export PDF
                                        </Button>
                                        <Button
                                            icon={<FileExcelOutlined />}
                                            onClick={exportToExcel}
                                            loading={exporting}
                                        >
                                            Export Excel
                                        </Button>
                                    </Space>
                                </div>

                                <div id="report-content" style={{
                                    background: '#fafafa',
                                    padding: '24px',
                                    borderRadius: '8px',
                                    border: '1px solid #e8e8e8'
                                }}>
                                    {/* Header */}
                                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                        <Title level={3} style={{ margin: 0 }}>
                                            MONTHLY TIME TRACKING REPORT - {reportData.month_name} {reportData.year}
                                        </Title>
                                        <Text type="secondary">{reportData.contract_name || 'Contract'}</Text>
                                        <div>
                                            <Text type="secondary">Reporting Period: {reportData.month_name} {reportData.year}</Text>
                                        </div>
                                    </div>

                                    {/* Grand Total Summary - Only show if there's data */}
                                    {(reportData.grand_total > 0 || 
                                      reportData.cr_summary?.length > 0 || 
                                      reportData.cm_summary?.length > 0 || 
                                      reportData.pm_summary?.length > 0 || 
                                      reportData.oth_summary?.length > 0) && (
                                        <Alert
                                            message={
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                                                    <Space size="large">
                                                        <div>
                                                            <Text strong>Grand Total:</Text>
                                                            <Text strong style={{ fontSize: '18px', color: '#1890ff', marginLeft: 8 }}>
                                                                {reportData.grand_total_display || '0 hrs'}
                                                            </Text>
                                                        </div>
                                                        <div>
                                                            <Text strong>Man Days:</Text>
                                                            <Text strong style={{ fontSize: '18px', color: '#fa8c16', marginLeft: 8 }}>
                                                                {reportData.grand_man_days_display || '0 days'}
                                                            </Text>
                                                        </div>
                                                    </Space>
                                                    <Space>
                                                        {reportData.cr_total_hours > 0 && (
                                                            <Tag color="blue">CR: {reportData.cr_total_hours} hrs ({reportData.cr_man_days} days)</Tag>
                                                        )}
                                                        {reportData.cm_total_hours > 0 && (
                                                            <Tag color="green">CM: {reportData.cm_total_hours} hrs ({reportData.cm_man_days} days)</Tag>
                                                        )}
                                                        {reportData.pm_total_hours > 0 && (
                                                            <Tag color="gold">PM: {reportData.pm_total_hours} hrs ({reportData.pm_man_days} days)</Tag>
                                                        )}
                                                        {reportData.oth_total_hours > 0 && (
                                                            <Tag color="default">NCM: {reportData.oth_total_hours} hrs ({reportData.oth_man_days} days)</Tag>
                                                        )}
                                                    </Space>
                                                </div>
                                            }
                                            type="info"
                                            showIcon
                                            style={{ marginBottom: 24 }}
                                        />
                                    )}

                                    {/* FIX: Only render sections with data */}
                                    {renderTableSection(
                                        'ACTIVITY CR', '🔄', 'blue',
                                        reportData.cr_summary, crSummaryColumns,
                                        reportData.cr_total_hours, reportData.cr_man_days
                                    )}

                                    {renderTableSection(
                                        'ACTIVITY CM', '🔧', 'green',
                                        reportData.cm_summary, cmSummaryColumns,
                                        reportData.cm_total_hours, reportData.cm_man_days
                                    )}

                                    {renderTableSection(
                                        'ACTIVITY PM', '🛡️', 'gold',
                                        reportData.pm_summary, pmSummaryColumns,
                                        reportData.pm_total_hours, reportData.pm_man_days
                                    )}

                                    {renderTableSection(
                                        'ACTIVITY NCM', '📋', 'default',
                                        reportData.oth_summary, othSummaryColumns,
                                        reportData.oth_total_hours, reportData.oth_man_days
                                    )}

                                    {/* Footer */}
                                    <div style={{
                                        marginTop: 24,
                                        paddingTop: 16,
                                        borderTop: '1px solid #e8e8e8',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '12px',
                                        color: '#8c8c8c'
                                    }}>
                                        <span>Generated by: {reportData.generated_by || 'System'}</span>
                                        <span>Date: {reportData.generated_date || moment().tz('Asia/Kuala_Lumpur').format('DD/MM/YYYY HH:mm')}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default ReportGeneratorMonthly;